import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';
//components
import Button from 'src/common/Button';
//styles
import { BreakPoints, Colors } from 'src/configs/StyleConfig';
import { ButtonMenu, SubmitButton, CancelButton } from 'src/common/StylesComponents';
//redux
import { useSelector, useDispatch } from 'react-redux';
import { addWarrantyData, setSelectedWarranty } from 'src/redux/actions/WarrantyHandler';
//functions
import DateFunctions from 'src/functions/DateFunctions';
import WarrantyFunctions from 'src/functions/WarrantyFunctions';
import { getCustomerNameFromId } from 'src/functions/CustomerFunctions';


const baseWarranty = {
  category: 0,
  store_contact: '',
  store_address: '',
  store_phone: '',
  store_name: '',
  customer_name: '',
  customer_address: '',
  customer_phone: '',
  construction_address: '',
  survey_date: null,
  construction_date: null,
  construction_type: null,
  construction_other: '',
  construction_division: null,
  termite_type: null,
  measures: 0,
  damage: '',
  building_area: 0,
  building_area_unit: '',
  chemical_name1: '',
  chemical_name2: '',
  chemical_name3: '',
  chemical_name4: '',
  chemical_name5: '',
  chemical_name6: '',
  chemical_used1: 0,
  chemical_used2: 0,
  chemical_used3: 0,
  chemical_used4: 0,
  chemical_used5: 0,
  chemical_used6: 0,
  chemical_remarks: '',
  certificate_no: '',
  guarantor: '',
  create_data: '',
  create_date: '',
  contract_no: '',
  work_area: 0,
  warranty_start_date: '',
  warranty_end_date: '',
  warranty_period: 5,
  dtb_construction_id: null,
  created_employee_name: '',
  updated_employee_name: '',
};

export const warrantyCategoryMap = {
  0: "シロアリ防除施工保証書",
  1: "防腐・防カビ保証書",
  2: "損害賠償保証書"
};

/*
  保証書リスト編集のコンポーネント
  複数ページにまたがって編集、追加を行うので、reduxのstoreに保証データを格納する
  保証証追加＝＞カテゴリに合わせて保証書をstoreに追加　→　その保証証ページに推移　
*/
const WarrantyListEdit = () => {
  //編集の場合、  dtb_construction_idを元にdtb_warrantyからフェッチ
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { selectedConstructionId, selectedConstructionData } = useSelector(state => state.construction);

  const { warranties } = useSelector(state => state.warranty);
  const { storeData } = useSelector(state => state.master);
  const { token, mtb_enterprise_id, employee_name } = useSelector(state => state.auth);

  const [isLoading, setIsLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const constructionData = useRef({ dtb_construction_id: null, certificate_no: null, contract_no: null, guarantor: null });

  useEffect(() => {

    /*
      保証情報の共通項目の設定
      値がある際は値をとり、ない時（工事登録時）はundefinedで工事登録の際に返り値の工事idと工事情報入力のその他項目（被保証人等）を設定する
    */
    const setConstructionGeneralData = async (dtb_construction_id) => {
      const contract_no = selectedConstructionData.contract_no;
      const mtb_customer_id = selectedConstructionData.mtb_customer_id;
      const guarantor = await getCustomerNameFromId(mtb_customer_id, mtb_enterprise_id, token); //顧客情報から顧客名を取ってきて補償者としておく
      constructionData.current.guarantor = guarantor;
      constructionData.current.dtb_construction_id = dtb_construction_id;
      constructionData.current.certificate_no = contract_no;
      constructionData.current.contract_no = contract_no;
    };

    if (selectedConstructionData) {
      const dtb_construction_id = selectedConstructionData.id;
      setConstructionGeneralData(dtb_construction_id);
      setIsLoading(false);
    }
  }, [selectedConstructionData]);

  /*
    選択、新規登録された保証証データに推移する関数
  */
  const navigateToEditWarranty = (warrantyType, index, isEditing) => {
    const currentPath = location.pathname;
    if (warrantyType === 2) {
      history.push({ pathname: `${currentPath}/${index}/damage-warranty-edit`, state: { isEditing: isEditing } });
    } else {
      history.push({ pathname: `${currentPath}/${index}/termite-antifungal-warranty-edit`, state: { isEditing: isEditing } });
    }
  };

  /*
    保証書追加の関数
    シロアリ、損害賠償、防かびそれぞれのボタンでカテゴリ分けされたデータをreduxのstore（warranties）に追加
    追加後、その編集ページへ推移
  */
  const addWarranty = useCallback((warrantyType) => {

    const getStoreDataForDamageWarranty = (warranty) => {
      let addingWarranty = { ...warranty };
      const { mtb_store_id, customer_name, customer_address1, customer_address2, customer_phone1 } = selectedConstructionData;
      const _storeData = storeData[mtb_store_id];
      if (_storeData) { //損害賠償保証に店舗データ格納
        addingWarranty.store_name = _storeData.store_name;
        addingWarranty.store_address = _storeData.address;
        addingWarranty.store_phone = _storeData.phone;
      }
      const address1 = customer_address1 ? customer_address1 : "";
      const address2 = customer_address2 ? customer_address2 : "";
      const address = address1 + " " + address2;
      addingWarranty.customer_name = customer_name;
      addingWarranty.customer_address = address
      addingWarranty.customer_phone = customer_phone1;
      addingWarranty.construction_address = address;
      return addingWarranty;
    };

    let addingWarranty = { ...baseWarranty };
    const d = new Date();
    const today = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0)); // 今日の日付＋12:00:00を設定
    
    const editingIndex = warranties.length;
    const isEditing = false; //registering
    addingWarranty.guarantor = constructionData.current.guarantor;
    addingWarranty.contract_no = constructionData.current.contract_no;
    addingWarranty.dtb_construction_id = constructionData.current.dtb_construction_id;
    addingWarranty.certificate_no = constructionData.current.contract_no;
    addingWarranty.warranty_start_date = today;
    addingWarranty.warranty_end_date = DateFunctions.addYears(DateFunctions.subtractDays(today, 1), 5);
    addingWarranty.create_date = today;
    if (warrantyType === "シロアリ") {
      addingWarranty.category = 0;
    } else if (warrantyType === "防腐・防カビ") {
      addingWarranty.category = 1;
    } else { //損害賠償保証
      addingWarranty.category = 2;
      addingWarranty.survey_date = today;
      addingWarranty.construction_date = today;
      addingWarranty.construction_type = -1;
      addingWarranty.termite_type = -1;
      addingWarranty.construction_division = -1;
      addingWarranty.measures = -1;
      addingWarranty = getStoreDataForDamageWarranty(addingWarranty)
    }
    dispatch(addWarrantyData(addingWarranty));
    dispatch(setSelectedWarranty(addingWarranty, editingIndex));
    navigateToEditWarranty(addingWarranty.category, editingIndex, isEditing);
  }, [constructionData, warranties, selectedConstructionData, storeData, dispatch, navigateToEditWarranty]);

  /*
    保証書編集ボタンプレス後の関数
    その編集ページへ推移
  */
  const editWarranty = useCallback((index) => {
    ///編集ボタンを押した保証証情報の編集ページへ移動
    const selectedWarranty = warranties[index] && warranties[index];
    const warrantyType = selectedWarranty.category;
    const isEditing = true;

    dispatch(setSelectedWarranty(selectedWarranty, index));
    navigateToEditWarranty(warrantyType, index, isEditing);
  }, [warranties, dispatch, navigateToEditWarranty]);

  /*
    保証書保存ボタンプレス後の関数
    保証データをDBに送る処理
  */
  const onSaveButtonPressed = useCallback(async () => {
    if (!employee_name) return;

    const warrantyFunctions = new WarrantyFunctions({
      warranty: warranties,
      dtb_construction_id: constructionData.current.dtb_construction_id,
      employee_name: employee_name,
      contract_no: constructionData.current.contract_no,
      token: token
    });
    const response = await warrantyFunctions.sendWarranties();
    if (response.error) {
      setErrorText(response.error);
    } else {
      setErrorText('');
      history.push(`/construction-list/${selectedConstructionId}/warranty`);
    }
  }, [warranties, constructionData, token, history, employee_name]);

  /*
    保証書リスト編集ページのキャンセルボタンプレス後の関数
    保証書リストページに戻る
  */
  const onCancelButtonPressed = useCallback(() => {
    history.push(`/construction-list/${selectedConstructionId}/warranty`);
  }, [history]);

  if (isLoading) {
    return (
      <Container>
        <div style={{ padding: '16px' }}>
          <Title>
            保証書
          </Title>
        </div>
      </Container>
    )
  };

  return (
    <Container>
      <div style={{ padding: '16px' }}>
        <Title>
          保証書
        </Title>
        <Add>
          <div>
            <p>新規追加</p>
            <Button text="シロアリ保証書（あんしん床下用）" width={180} onClick={() => addWarranty("シロアリ")} />
            <Button text="防腐・防カビ" width={180} onClick={() => addWarranty("防腐・防カビ")} />
            <Button text="損害賠償保証書（カインズなどの量販店用）" width={180} onClick={() => addWarranty("損害賠償保証")} />
          </div>
        </Add>
        <ListBody>
          {
            warranties && warranties.map((warranty, index) => {
              return (
                <Row style={{ 'position': 'relative' }} key={index.toString()}>
                  <ContentWrapper>
                    <div>
                      <p>証書番号：{warranty.certificate_no}</p>
                      <p>作成年月日：{DateFunctions.ISOStringDateToLocalDate(warranty.create_date)}</p>
                      <p>作業実施面積：{warranty.work_area}</p>
                    </div>
                    <div>
                      <p>保証期間：{DateFunctions.ISOStringDateToLocalDate(warranty.warranty_start_date)}　から  {warranty.warranty_period}年間</p>
                      <p>{DateFunctions.ISOStringDateToLocalDate(warranty.warranty_end_date)}　まで</p>
                    </div>
                  </ContentWrapper>
                  <ButtonWrapper>
                    <span style={{ 'display': 'flex', 'justifyContent': 'flex-end', 'padding': '8px' }}>
                      {warrantyCategoryMap[warranty.category]}
                    </span>
                    <div style={{ 'display': 'flex', 'flexDirection': 'row', 'justifyContent': 'flex-end' }}>
                      <Button text="編集" onClick={() => editWarranty(index)} />
                    </div>
                  </ButtonWrapper>
                </Row>
              )
            })
          }
        </ListBody>
      </div>
      <span style={{ 'color': Colors.errorText, 'width': '100%', 'display': 'flex', 'justifyContent': 'center' }}>
        {errorText && errorText}
      </span>
      <div style={{ 'width': '100%', 'display': 'flex', 'justifyContent': 'center', 'padding': '12px 0' }}>
        <ButtonMenu>
          <SubmitButton onClick={onSaveButtonPressed}>保存</SubmitButton>
          <CancelButton onClick={onCancelButtonPressed}>キャンセル</CancelButton>
        </ButtonMenu>
      </div>
    </Container>
  )
};

const Container = styled.div`
  max-width: 1200px;
  min-width: ${BreakPoints.md};
  width: 90%;
  margin: 20px auto;
`

const Title = styled.div`
  text-align: left;
  font-size: 24px;
`

const Add = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px 0;
`

const ContentWrapper = styled.div`
  width: 50%;
  min-width: 400px;
  display: flex;
  justify-content: space-between;
  padding-left: 40px;
`
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 0px;
  right: 0px; 
  width: 200px;
`

const ListBody = styled.div`
  border: 1px solid gray;
  width: 100%;
  height: 100%;
  min-height: 300px;
  overflow: auto;
  display: block;
`
const Row = styled.div`
  width: 98%;
  height: 60px;
  table-layout: fixed;
  display: table;
  font-size: 14px;
  margin: 0 auto;
  border-bottom: 1px solid lightgray;
`

export default WarrantyListEdit;
